/** Shared UTC schedule math for pseudo-live radio (mirrors API + media worker). */

export type RadioTrack = {
  id: string;
  name: string;
  duration: number;
  streamUrl: string;
  /** Catalog track # on the source release (credits / share), not mixtape rank. */
  position: number;
  /** Catalog release id — share / info deep links for the song on air. */
  releaseId?: string;
  albumName: string;
  artist: string;
  artworkUrl: string;
  releaseType: string;
  /** ReplayGain track gain (dB) — live player always applies this. */
  replayGain?: number;
  replayGainTrack?: number;
  replayGainAlbum?: number;
};

export type RadioSchedule = {
  serverNow: number;
  epochMs: number;
  version: string;
  /** Newfoundland calendar day (YYYY-MM-DD) that seeded playlist order. */
  dayKey?: string;
  /**
   * IANA timezone for the live footer clock (DST follows the zone’s rules).
   * Defaults to America/St_Johns when omitted.
   */
  clockTimezone?: string;
  /** Mixtape mood used to build today’s order */
  mood?: string;
  streamUrl: string;
  tracks: RadioTrack[];
};

export type RadioPosition = {
  index: number;
  track: RadioTrack;
  offsetSec: number;
  loopDuration: number;
  /** Seconds until this track ends on the wall clock. */
  remainingSec: number;
};

export function loopDurationSec(tracks: { duration: number }[]): number {
  let total = 0;
  for (const t of tracks) {
    const d = Number(t.duration) || 0;
    if (d > 0) total += d;
  }
  return total;
}

export function resolveRadioPosition(
  tracks: RadioTrack[],
  epochMs: number,
  nowMs: number,
): RadioPosition | null {
  if (!tracks.length) return null;
  const loopDuration = loopDurationSec(tracks);
  if (loopDuration <= 0) return null;

  let elapsed = ((nowMs - epochMs) / 1000) % loopDuration;
  if (elapsed < 0) elapsed = ((elapsed % loopDuration) + loopDuration) % loopDuration;

  for (let i = 0; i < tracks.length; i++) {
    const track = tracks[i]!;
    const d = Number(track.duration) || 0;
    if (d <= 0) continue;
    if (elapsed < d) {
      return {
        index: i,
        track,
        offsetSec: elapsed,
        loopDuration,
        remainingSec: d - elapsed,
      };
    }
    elapsed -= d;
  }

  const lastIndex = tracks.length - 1;
  const last = tracks[lastIndex]!;
  return {
    index: lastIndex,
    track: last,
    offsetSec: 0,
    loopDuration,
    remainingSec: Number(last.duration) || 0,
  };
}

/** Wall-clock now adjusted by skew captured from API serverNow. */
export function radioNowMs(skewMs: number): number {
  return Date.now() + skewMs;
}

/** Playhead clock: live wall time minus timeshift delay (0 = live). */
export function radioPlayNowMs(skewMs: number, timeshiftMs: number): number {
  return radioNowMs(skewMs) - Math.max(0, timeshiftMs);
}

export function nextRadioIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return (index + 1) % length;
}

/** Format timeshift delay for UI, e.g. −1:24 */
export function formatRadioDelay(timeshiftMs: number): string {
  const totalSec = Math.max(0, Math.ceil(timeshiftMs / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `−${m}:${String(s).padStart(2, '0')}`;
}

/** Default live-clock timezone (Newfoundland — observes NST/NDT). */
export const DEFAULT_CLOCK_TZ = 'America/St_Johns';

/** True when `tz` is a valid IANA timezone id. */
export function isValidTimezone(tz: string): boolean {
  if (!tz) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Normalize to a valid IANA zone; falls back to {@link DEFAULT_CLOCK_TZ}. */
export function normalizeClockTimezone(tz: unknown): string {
  if (typeof tz !== 'string') return DEFAULT_CLOCK_TZ;
  const value = tz.trim();
  return isValidTimezone(value) ? value : DEFAULT_CLOCK_TZ;
}

/**
 * Wall clock as HH:MM (24-hour) in the given IANA timezone.
 * DST is applied automatically when the region observes it.
 */
export function formatClock(
  ms: number = Date.now(),
  timeZone: string = DEFAULT_CLOCK_TZ,
): string {
  const tz = normalizeClockTimezone(timeZone);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
}

/** Media Session / UI end-of-day duration (matches desktop “24:00”). */
export const RADIO_DAY_DURATION_SEC = 24 * 60 * 60;

/**
 * Seconds since local midnight in `timeZone` (0 … 86399).
 * Used so lock-screen Now Playing can mirror the desktop day clock.
 */
export function secondsSinceMidnight(
  ms: number = Date.now(),
  timeZone: string = DEFAULT_CLOCK_TZ,
): number {
  const tz = normalizeClockTimezone(timeZone);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  const second = Number(parts.find((p) => p.type === 'second')?.value ?? 0);
  const total = hour * 3600 + minute * 60 + second;
  if (!Number.isFinite(total) || total < 0) return 0;
  return Math.min(total, RADIO_DAY_DURATION_SEC - 1);
}

/** Max how far behind live the listener can scrub (1 hour). */
export const RADIO_TIMESHIFT_MAX_MS = 60 * 60 * 1000;

/** Skip back / toward-live step. */
export const RADIO_SKIP_STEP_MS = 15 * 1000;

/** Only reload live when lag exceeds this; smaller drift is left alone. */
export const RADIO_DRIFT_RELOAD_SEC = 12;
